import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { SiteData, SimulationStatistic } from '../types';
import { fetchGoogleSheetsData, fetchSimulationStatistics } from '../utils/csvParser';

interface DataContextType {
  siteData: SiteData[];
  simulationStats: SimulationStatistic[];
  uploadedFile: File | null;
  isDataPublished: boolean;
  isLoading: boolean;
  error: string | null;
  uploadFile: (file: File, data: SiteData[]) => void;
  publishData: () => void;
  clearData: () => void;
  refreshData: () => Promise<void>;
}

const DataContext = createContext<DataContextType | undefined>(undefined);

export const useData = () => {
  const context = useContext(DataContext);
  if (!context) {
    throw new Error('useData must be used within a DataProvider');
  }
  return context;
};

interface DataProviderProps {
  children: ReactNode;
}

export const DataProvider: React.FC<DataProviderProps> = ({ children }) => {
  const [siteData, setSiteData] = useState<SiteData[]>([]);
  const [simulationStats, setSimulationStats] = useState<SimulationStatistic[]>([]);
  const [uploadedFile, setUploadedFile] = useState<File | null>(null);
  const [isDataPublished, setIsDataPublished] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Load data on component mount
  const loadData = async () => {
    try {
      setIsLoading(true);
      setError(null);
      const [siteData, simulationData] = await Promise.all([
        fetchGoogleSheetsData(),
        fetchSimulationStatistics()
      ]);
      setSiteData(siteData);
      setSimulationStats(simulationData);
      setIsDataPublished(true); // Auto-publish data
      console.log(`Loaded ${siteData.length} sites and ${simulationData.length} simulation statistics`);
    } catch (err) {
      console.error('Failed to load data:', err);
      setError('Failed to load data from data source');
    } finally {
      setIsLoading(false);
    }
  };

  // Load data on mount
  useEffect(() => {
    loadData();
  }, []);

  const uploadFile = (file: File, data: SiteData[]) => {
    setUploadedFile(file);
    setSiteData(data);
    setIsDataPublished(false);
    setError(null);
  };

  const publishData = () => {
    setIsDataPublished(true);
  };

  const clearData = () => {
    setSiteData([]);
    setUploadedFile(null);
    setIsDataPublished(false);
    setError(null);
  };

  const refreshData = async () => {
    await loadData();
  };

  return (
    <DataContext.Provider value={{
      siteData,
      simulationStats,
      uploadedFile,
      isDataPublished,
      isLoading,
      error,
      uploadFile,
      publishData,
      clearData,
      refreshData
    }}>
      {children}
    </DataContext.Provider>
  );
};